// Khối "Thanh toán NCC" trên phiếu nhập ĐÃ XÁC NHẬN — sổ chi quỹ.
// Hiện tổng phiếu / đã trả / còn lại + danh sách khoản chi + form thêm khoản chi.
// Chi/xóa tiền: manager+ (BE cũng chặn). Warehouse chỉ xem.
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Banknote, Trash2 } from "lucide-react";
import { apiClient } from "@/lib/api/client";
import { translateApiError } from "@/lib/errors/error-map";
import { useAuthStore } from "@/lib/auth/store";
import { Button } from "@/components/ui/button";
import { MoneyInput } from "@/components/ui/money-input";
import { formatDateTime } from "@/lib/format/date";

export interface SupplierPayment {
  id: string;
  supplierId: string;
  supplierName: string;
  importId?: string | null;
  importCode?: string | null;
  amount: string;
  method: string;
  note: string;
  paidByEmail?: string | null;
  paidAt: string;
}

const METHOD_LABEL: Record<string, string> = {
  cash: "Tiền mặt",
  bank_manual: "Chuyển khoản",
};

function fmtVND(n: number) {
  return new Intl.NumberFormat("vi-VN").format(Math.round(n)) + " ₫";
}

export function ImportPaymentsSection({
  importId,
  totalCost,
  supplierName,
  debtBooked,
}: {
  importId: string;
  totalCost: string;
  supplierName: string;
  debtBooked: boolean;
}) {
  const qc = useQueryClient();
  const user = useAuthStore((s) => s.user);
  // sales (chị Lành) được chi trả NCC từ 28/07; xóa khoản chi vẫn manager+.
  const canPay = user?.role === "admin" || user?.role === "manager" || user?.role === "sales";
  const canDeletePayment = user?.role === "admin" || user?.role === "manager";

  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState<"cash" | "bank_manual">("cash");
  const [note, setNote] = useState("");
  const [error, setError] = useState("");

  const { data: payments = [] } = useQuery({
    queryKey: ["import-payments", importId],
    queryFn: () =>
      apiClient
        .get<{ data: SupplierPayment[] }>(`/v1/imports/${importId}/payments`)
        .then((r) => r.data.data),
  });

  const total = parseFloat(totalCost) || 0;
  const paid = payments.reduce((acc, p) => acc + (parseFloat(p.amount) || 0), 0);
  const remaining = Math.max(0, total - paid);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["import-payments", importId] });
    qc.invalidateQueries({ queryKey: ["suppliers"] });
  };

  const addMutation = useMutation({
    mutationFn: (body: { amount: number; method: string; note: string }) =>
      apiClient.post(`/v1/imports/${importId}/payments`, body),
    onSuccess: () => {
      setAmount("");
      setNote("");
      setError("");
      invalidate();
    },
    onError: (e) => setError(translateApiError(e)),
  });

  const deleteMutation = useMutation({
    mutationFn: (paymentId: string) =>
      apiClient.delete(`/v1/supplier-payments/${paymentId}`),
    onSuccess: () => {
      setError("");
      invalidate();
    },
    onError: (e) => setError(translateApiError(e)),
  });

  return (
    <div className="rounded-md border p-4 space-y-3">
      <div className="flex items-center gap-2 font-semibold">
        <Banknote size={18} className="text-green-600" />
        Thanh toán NCC {supplierName ? `— ${supplierName}` : ""}
      </div>

      <div className="grid grid-cols-3 gap-2 text-sm">
        <div className="rounded bg-muted/40 p-2">
          <p className="text-xs text-muted-foreground">Tổng phiếu</p>
          <p className="font-semibold tabular-nums">{fmtVND(total)}</p>
        </div>
        <div className="rounded bg-muted/40 p-2">
          <p className="text-xs text-muted-foreground">Đã trả</p>
          <p className="font-semibold text-green-600 tabular-nums">{fmtVND(paid)}</p>
        </div>
        <div className="rounded bg-muted/40 p-2">
          <p className="text-xs text-muted-foreground">Còn lại</p>
          <p className={`font-semibold tabular-nums ${remaining > 0 ? "text-destructive" : "text-green-600"}`}>
            {fmtVND(remaining)}
          </p>
        </div>
      </div>

      {!debtBooked && (
        <p className="text-xs text-muted-foreground">
          Phiếu này xác nhận trước khi có sổ chi nên tiền hàng CHƯA tự cộng vào công nợ NCC.
          Thanh toán ở đây vẫn ghi sổ chi và trừ công nợ NCC như thường.
        </p>
      )}

      {payments.length > 0 && (
        <table className="w-full text-sm">
          <thead className="border-b text-xs uppercase text-muted-foreground">
            <tr>
              <th className="py-1 text-left">Thời gian</th>
              <th className="py-1 text-right">Số tiền</th>
              <th className="py-1 text-left pl-3">Phương thức</th>
              <th className="py-1 text-left">Người chi</th>
              <th className="py-1 text-left">Ghi chú</th>
              {canDeletePayment && <th className="py-1" />}
            </tr>
          </thead>
          <tbody>
            {payments.map((p) => (
              <tr key={p.id} className="border-b last:border-0">
                <td className="py-1.5">{formatDateTime(p.paidAt)}</td>
                <td className="py-1.5 text-right font-semibold tabular-nums">
                  {fmtVND(parseFloat(p.amount) || 0)}
                </td>
                <td className="py-1.5 pl-3">{METHOD_LABEL[p.method] ?? p.method}</td>
                <td className="py-1.5 text-xs text-muted-foreground">{p.paidByEmail ?? "—"}</td>
                <td className="py-1.5 text-xs">{p.note || "—"}</td>
                {canDeletePayment && (
                  <td className="py-1.5 text-right">
                    <button
                      type="button"
                      title="Xóa khoản chi (hoàn lại công nợ NCC)"
                      onClick={() => {
                        if (confirm("Xóa khoản thanh toán này? Số tiền sẽ được cộng lại vào công nợ NCC.")) {
                          deleteMutation.mutate(p.id);
                        }
                      }}
                      className="text-destructive hover:opacity-70"
                    >
                      <Trash2 size={15} />
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {canPay && remaining > 0 && (
        <div className="space-y-2 rounded bg-muted/20 p-3">
          <p className="text-sm font-medium">Thêm khoản thanh toán</p>
          <div className="flex flex-wrap items-center gap-2">
            <MoneyInput
              value={amount}
              onChange={setAmount}
              placeholder="Số tiền"
              className="w-36"
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setAmount(String(Math.round(remaining)))}
            >
              Trả đủ ({fmtVND(remaining)})
            </Button>
            <select
              value={method}
              onChange={(e) => setMethod(e.target.value as "cash" | "bank_manual")}
              className="h-9 rounded-md border bg-background px-2 text-sm"
            >
              <option value="cash">Tiền mặt</option>
              <option value="bank_manual">Chuyển khoản</option>
            </select>
            <input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Ghi chú (tùy chọn)"
              className="h-9 min-w-[160px] flex-1 rounded-md border bg-background px-2 text-sm"
            />
            <Button
              size="sm"
              disabled={addMutation.isPending || !amount || parseFloat(amount) <= 0}
              onClick={() =>
                addMutation.mutate({ amount: parseFloat(amount), method, note })
              }
            >
              {addMutation.isPending ? "Đang ghi…" : "Ghi chi"}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Khoản chi sẽ trừ công nợ NCC và hiện trong báo cáo Quỹ tiền (chiều CHI).
          </p>
        </div>
      )}

      {error && (
        <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</p>
      )}
    </div>
  );
}
